/*
  Add-to-cart flight.

  On every add, a copy of the product lifts off THE PRODUCT IMAGE ITSELF and
  travels along a curved path to the cart icon in the header, shrinking as it
  goes, and the cart icon pops when it lands.

  The flight starts at the image rather than at the button, and at the image's
  own rendered size, so the first frame is indistinguishable from the photo on
  the page: it reads as that product being taken into the cart rather than as
  a token appearing out of a control. The button is only the fallback origin,
  for a card with no usable photo.

  Sibling of the heart flight in assets/wishlist.js — same arc maths, same
  landing guard. Kept separate because that one is wired into the wishlist
  store, while this one is purely visual and has no state to own.

  WHY A DELEGATED CAPTURE LISTENER, rather than a hook inside product-form.js:
  add-to-cart is not one control on this theme. There is Dawn's <product-form>
  on the product page and in featured-product blocks, the bespoke cards in
  sections/feature_products.liquid, the quick-add in card-product.liquid, and
  on `use-gpo` products the options app takes the click over entirely. A
  listener on the document in the CAPTURE phase runs before any of them and
  regardless of whether they stop propagation, so all of those are covered by
  one place instead of five.

  The flight starts on the click, not on the server's response: it is meant to
  acknowledge the press. Only the cart's pop waits for the arrival.

  The one thing it does wait for is a refusal. A press that is rejected before
  anything is added — a required option left unanswered — used to fly a parcel
  at the cart anyway, which told the shopper the exact opposite of what had
  happened. See `addWasRefused` below for how that is detected without giving
  up the press-time start.
*/
(function () {
  var FLIGHT_MS = 1150;
  var FLY_SIZE = 64; // px; fallback size only — a real flight matches the image
  var CART_GLYPH =
    'M20.5 6.5a4.75 4.75 0 00-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 004.99 5.34h7.38a5 5 0 004.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0020.5 6.5z';

  /* Anything that leaves the page or is not an add. The dynamic checkout
     buttons sit inside the same form as Add to cart, so they have to be
     excluded by hand or Buy-with-ShopPay would fly a product at the cart on
     its way to the checkout. */
  var IGNORE = '.shopify-payment-button, [name="checkout"], [data-no-cart-fly]';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function centreOf(element) {
    var rect = element.getBoundingClientRect();
    if (!rect.width && !rect.height) return null; // display:none, or off-layout
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, w: rect.width };
  }

  /* NOT the product: anything decorative that happens to be an <img> near the
     button. This list is the fix for a real bug — the Pass or Don't Pay seal
     (`img.pdp2-price-seal`) sits in the buy box next to the price, so a plain
     "first image ≥40px" walk found IT before it ever reached the gallery, and
     every add-to-cart flew the promise badge at the cart.

     Matched against the image itself AND its ancestors, because some of these
     carry the class on the <img> (the seal) and some on a wrapper (the option
     swatches, which live inside `.gpo-container`). */
  var NOT_PRODUCT = [
    '[class*="seal"]',
    '[class*="promise"]',
    '[class*="badge"]',
    '[class*="swatch"]',
    '[class*="review"]',
    '[class*="star"]',
    '[class*="payment"]',
    '[class*="logo"]',
    '[class*="icon"]',
    '[class*="spinner"]',
    '.gpo-container'
  ].join(',');

  /* Tried in order at each level of the walk, most specific first. The main
     stage image beats a thumbnail of the same product, and either beats a
     bare <img> that merely happens to be nearby. */
  var PRODUCT_IMAGE = [
    /* This theme's own main-image hooks, named outright. The PDP hero's stage
       image carries `data-pdp-stage-image` and no class of its own
       (sections/mt-pdp-hero.liquid), so naming the attribute is exact where a
       substring match on "stage" only works by luck of the wrapper's name. */
    '[data-pdp-stage-image], .pdp2-stage img, .mt-fk-photo img, .card__media img, .product__media img',
    '[class*="stage"] img, [class*="photo"] img',
    '[class*="gallery"] img, [class*="media"] img, picture img',
    'img'
  ];

  function usable(image) {
    if (!image) return false;
    if (image.matches(NOT_PRODUCT) || image.closest(NOT_PRODUCT)) return false;
    if (!(image.currentSrc || image.src)) return false;

    var rect = image.getBoundingClientRect();
    return rect.width >= 40 && rect.height >= 40;
  }

  function firstUsable(scope, selector) {
    var images = scope.querySelectorAll(selector);

    for (var i = 0; i < images.length; i += 1) {
      if (usable(images[i])) return images[i];
    }

    return null;
  }

  /* The nearest product image, found by walking up from the button.

     Walking outward rather than querying a fixed selector is what makes this
     work on every surface at once: in a grid the card is hit first and its own
     photo wins, while on a product page nothing in the buy box qualifies, so
     the walk carries on up to the hero grid and takes the stage image. */
  function findImage(button) {
    var node = button;

    /* 14, not 8. On the product page the Add to Cart button sits nine levels
       down — button > div > form > product-form > div > div > div > section —
       and the gallery is a sibling several levels above that. A limit of 8
       stopped the walk before it ever reached the hero grid, found nothing,
       and fell through to the cart glyph. That was the "it flies a cart icon,
       not the product" bug. */
    for (var depth = 0; node && depth < 14; depth += 1) {
      if (node.querySelectorAll) {
        for (var tier = 0; tier < PRODUCT_IMAGE.length; tier += 1) {
          var found = firstUsable(node, PRODUCT_IMAGE[tier]);
          if (found) return found;
        }
      }

      node = node.parentElement;
    }

    /* Last resort before the glyph: the page's main product image, wherever it
       is. The walk is what keeps a grid of cards flying the RIGHT product, so
       it stays the primary path — but on a page with one product and one
       gallery, a document-wide lookup cannot pick the wrong one, and it is
       better than falling back to an icon. */
    return firstUsable(
      document,
      '[data-pdp-stage-image], .pdp2-stage img, .product__media img, .card__media img'
    );
  }

  /* Samples a quadratic Bezier into keyframes, because the endpoints are only
     known at click time and cannot be written as CSS.

     THREE PHASES, so it reads as someone picking the product up and putting it
     in the cart rather than as a thumbnail sliding across the screen. A single
     eased tween from A to B cannot say that — the hand has to pause on the
     product before it carries it, and the product has to go down INTO the cart
     rather than simply stop existing over it.

       0–16%    PICK UP   rises off the page, swells slightly, tilts in hand.
                          No lateral movement at all yet: this beat is what
                          reads as "taken", and it is why the whole thing is
                          legible instead of a blur.
       16–86%   CARRY     travels the arc, shrinking, the lift decaying and
                          the tilt levelling out as it goes.
       86–100%  DROP IN   holds over the cart and drops the last few pixels
                          into it, shrinking past its landing size and fading
                          only at the very end, so it is visible right up to
                          the moment it is in.

     The control point is lifted above both ends, which turns a straight
     diagonal into a lob: the parcel rises off the product and comes down onto
     the cart rather than sliding across the page. The lift scales with the
     distance, with a floor, so a product beside the header still hops. */
  var LIFT_END = 0.16;
  var CARRY_END = 0.86;
  var PICK_UP_PX = 20;

  function arcFrames(from, to, endScale) {
    var dx = to.x - from.x;
    var dy = to.y - from.y;
    var distance = Math.sqrt(dx * dx + dy * dy);
    var lift = Math.max(80, distance * 0.32);

    var control = { x: from.x + dx * 0.35, y: Math.min(from.y, to.y) - lift };
    var carried = endScale * 1.3; // size on arrival, before it drops in
    var frames = [];
    var steps = 34;

    for (var i = 0; i <= steps; i += 1) {
      var t = i / steps;
      var progress;
      var hover;
      var scale;
      var tilt;
      var opacity = 1;
      var shadow = 0.45;

      if (t <= LIFT_END) {
        var lifting = t / LIFT_END;
        progress = 0;
        hover = -PICK_UP_PX * lifting;
        scale = 1 + 0.12 * lifting;
        tilt = -6 * lifting;
        shadow = 0.45 + 0.2 * lifting;
      } else if (t <= CARRY_END) {
        var carrying = (t - LIFT_END) / (CARRY_END - LIFT_END);
        progress = carrying;
        hover = -PICK_UP_PX * (1 - carrying);
        scale = 1.12 - (1.12 - carried) * carrying;
        tilt = -6 * (1 - carrying);
        shadow = 0.65 - 0.35 * carrying;
      } else {
        var dropping = (t - CARRY_END) / (1 - CARRY_END);
        progress = 1;
        hover = 8 * dropping;
        scale = carried - (carried - endScale * 0.55) * dropping;
        tilt = 0;
        opacity = 1 - dropping * dropping;
        shadow = 0.3 * (1 - dropping);
      }

      var inverse = 1 - progress;
      var x =
        inverse * inverse * from.x + 2 * inverse * progress * control.x + progress * progress * to.x;
      var y =
        inverse * inverse * from.y + 2 * inverse * progress * control.y + progress * progress * to.y;

      frames.push({
        offset: t,
        transform:
          'translate(-50%, -50%) translate(' +
          (x - from.x) +
          'px, ' +
          (y - from.y + hover) +
          'px) scale(' +
          scale +
          ') rotate(' +
          tilt +
          'deg)',
        opacity: opacity,
        boxShadow: '0 ' + (1.2 * scale).toFixed(2) + 'rem 3rem rgba(0, 0, 0, ' + shadow.toFixed(2) + ')'
      });
    }

    return frames;
  }

  function popCart(target) {
    if (!target) return;
    target.classList.remove('is-receiving');
    // Reflow, so a second add mid-animation restarts the pop instead of being
    // swallowed by the class already being on the element.
    void target.offsetWidth;
    target.classList.add('is-receiving');
    setTimeout(function () {
      target.classList.remove('is-receiving');
    }, 520);
  }

  function fly(button) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    var image = findImage(button);

    /* The product is the origin. Only when there is no photo to lift does the
       button stand in — a parcel with nothing in it may as well come from the
       control that was pressed. */
    var from = centreOf(image || button);
    var to = centreOf(target);
    if (!from || !to) return;

    /* Matched to the image on the page, so the first frame of the flight is
       the photo the shopper is already looking at. Clamped: a full-width
       gallery shot would otherwise launch a 500px square across the screen,
       and a thumbnail would launch something too small to read. */
    var size = FLY_SIZE;

    if (image) {
      var bounds = image.getBoundingClientRect();
      size = Math.max(56, Math.min(140, Math.min(bounds.width, bounds.height)));
    }

    var parcel = document.createElement('span');
    parcel.className = 'mt-cart-fly';
    parcel.setAttribute('aria-hidden', 'true');
    parcel.style.left = from.x + 'px';
    parcel.style.top = from.y + 'px';
    parcel.style.width = size + 'px';
    parcel.style.height = size + 'px';

    if (image) {
      var copy = document.createElement('img');
      copy.src = image.currentSrc || image.src;
      copy.alt = '';
      parcel.appendChild(copy);
    } else {
      /* No photo to hand — a quick-add on a card with no image, or a product
         page that has not loaded one. The cart glyph carries the same meaning. */
      parcel.classList.add('mt-cart-fly--glyph');
      parcel.innerHTML =
        '<svg viewBox="0 0 40 40" focusable="false"><path d="' + CART_GLYPH + '"/></svg>';
    }

    document.body.appendChild(parcel);

    if (typeof parcel.animate !== 'function') {
      parcel.parentNode.removeChild(parcel);
      popCart(target);
      return;
    }

    // Land at roughly the size of the cart glyph itself, measured against
    // whatever size this particular parcel started at.
    var endScale = Math.max(0.12, Math.min(0.4, (to.w || 20) / size));

    /* `linear`, deliberately. The phase boundaries above are positions in
       time — an eased effect would slide them around and swallow the pick-up
       beat, which is the part that makes the whole thing readable. The shaping
       lives inside each phase instead. */
    var animation = parcel.animate(arcFrames(from, to, endScale), {
      duration: FLIGHT_MS,
      easing: 'linear',
      fill: 'forwards'
    });

    /* One landing, whichever comes first. `finish` is the normal path; the
       timeout covers a browser that throttles or drops the animation — a
       backgrounded tab, mostly — where the parcel would otherwise be left on
       the page for good. */
    var landed = false;
    var popped = false;

    /* The pop fires as the drop-in starts, not when the animation ends: the
       cart has to react while the product is going in, or the bounce reads as
       an unrelated twitch a beat later. */
    function arrive() {
      if (popped) return;
      popped = true;
      popCart(target);
    }

    var arriveTimer = setTimeout(arrive, Math.round(FLIGHT_MS * CARRY_END));

    function land() {
      if (landed) return;
      landed = true;
      clearTimeout(arriveTimer);
      arrive();
      if (parcel.parentNode) parcel.parentNode.removeChild(parcel);
    }

    animation.addEventListener('finish', land);
    setTimeout(land, FLIGHT_MS + 250);
  }

  /* Was this press refused before anything could be added? Two sources:

     - The browser's own constraint validation, for native `required` fields.
     - The options app's message. Globo rejects a group by inserting
       `.help-block.error` into it, and it does that synchronously while the
       click is still being dispatched. It also removes those nodes the moment
       the field is satisfied, so a stale one cannot suppress a later flight.

     Scoped to the form that was submitted, so a message somewhere else on the
     page cannot hold back a legitimate flight. */
  function addWasRefused(button, host) {
    var form = button.form || (host.tagName === 'FORM' ? host : host.querySelector('form'));

    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return true;

    var message = (form || host).querySelector('.help-block.error');
    return !!(message && (message.offsetWidth || message.offsetHeight));
  }

  document.addEventListener(
    'click',
    function (event) {
      if (prefersReducedMotion()) return;

      var button = event.target.closest('button, input[type="submit"]');
      if (!button || button.closest(IGNORE)) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      // It has to be an add, and it has to be inside something that adds.
      var host = button.closest('product-form, form[action*="/cart/add"]');
      if (!host) return;
      if (button.type !== 'submit' && button.name !== 'add') return;

      /* Scheduled rather than flown here. This listener is on the CAPTURE
         phase, so at this instant the page's own submit handling has not run
         and a refusal has not been recorded yet. A zero-delay timeout resumes
         once the click has finished dispatching — late enough to see the
         refusal, and early enough that the press still feels acknowledged. */
      setTimeout(function () {
        if (addWasRefused(button, host)) return;
        fly(button);
      }, 0);
    },
    true
  );
})();
